/**
 * trigger-list.tsx — Table of configured escalation triggers with enable/disable actions.
 *
 * AC 1, 3: shows triggers, enable/disable, view history button.
 */

import { Table } from '@nrx/ui';
import type { JSX } from 'react';

import type { EscalationTriggerRecord } from './escalation-params-api.js';

interface TriggerListProps {
  triggers: EscalationTriggerRecord[];
  onEdit: (trigger: EscalationTriggerRecord) => void;
  onDisable: (triggerId: string) => void;
  onEnable: (triggerId: string) => void;
  onViewHistory: (trigger: EscalationTriggerRecord) => void;
  disabling: string | null;
  loading?: boolean;
}

export function TriggerList({
  triggers,
  onEdit,
  onDisable,
  onEnable,
  onViewHistory,
  disabling,
  loading = false,
}: TriggerListProps): JSX.Element {
  if (!loading && triggers.length === 0) {
    return (
      <p className="text-gray-500 text-sm py-4">
        No escalation triggers configured. Click &quot;Add Trigger&quot; to create one.
      </p>
    );
  }

  return (
    <Table.ScrollArea loading={loading} loadingLabel="Loading triggers…">
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Tier</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Cooldown</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Version</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {triggers.map((trigger) => (
            <Table.Row key={trigger.id} className={trigger.enabled ? '' : 'opacity-50'}>
              <Table.Cell className="font-medium">{trigger.name}</Table.Cell>
              <Table.Cell>
                <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                  {trigger.type}
                </span>
              </Table.Cell>
              <Table.Cell>
                <span
                  className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
                    trigger.destinationTier === 'emergency'
                      ? 'bg-red-100 text-red-700'
                      : trigger.destinationTier === 'md'
                        ? 'bg-orange-100 text-orange-700'
                        : trigger.destinationTier === 'rd'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {trigger.destinationTier.toUpperCase()}
                </span>
              </Table.Cell>
              <Table.Cell className="text-gray-600">{trigger.cooldownHours}h</Table.Cell>
              <Table.Cell>
                {trigger.enabled ? (
                  <span className="font-medium text-green-700">Active</span>
                ) : (
                  <span className="text-gray-400">Disabled</span>
                )}
              </Table.Cell>
              <Table.Cell className="text-gray-500">v{trigger.version}</Table.Cell>
              <Table.Cell>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => onEdit(trigger)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => onViewHistory(trigger)}
                    className="text-xs text-gray-600 hover:text-gray-800"
                  >
                    View history
                  </button>
                  {trigger.enabled ? (
                    <button
                      type="button"
                      onClick={() => onDisable(trigger.id)}
                      disabled={disabling === trigger.id}
                      className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      {disabling === trigger.id ? 'Disabling...' : 'Disable'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onEnable(trigger.id)}
                      disabled={disabling === trigger.id}
                      className="text-xs text-green-700 hover:text-green-900 disabled:opacity-50"
                    >
                      {disabling === trigger.id ? 'Enabling...' : 'Enable'}
                    </button>
                  )}
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Table.ScrollArea>
  );
}
